import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Search, Sparkles, Shield, Zap, ArrowRight } from "lucide-react";
import { EventCard } from "@/components/EventCard";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice, formatDate, categories } from "@/lib/mock-data";
import { getPublicEventsList } from "@/lib/events-public.functions";
import heroBg from "@/assets/hero-bg.jpg";
import type { Tables } from "@/integrations/supabase/types";

type EventWithTicketTypes = Tables<"events"> & {
  ticket_types: Tables<"ticket_types">[];
};

export const Route = createFileRoute("/")({
  component: Index,
});

function Index() {
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [searchQuery, setSearchQuery] = useState("");
  const [events, setEvents] = useState<EventWithTicketTypes[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    setLoading(true);
    try {
      // Lectura cacheada vía server fn (KV cuando esté enlazado, memoria
      // por isolate como fallback). TTL 60s.
      const data = (await getPublicEventsList()) as EventWithTicketTypes[];
      setEvents(data || []);
    } catch (error) {
      console.error("Error loading events:", error);
      // Fallback duro a lectura directa (sin cache) si la server fn falla.
      const { data } = await supabase
        .from("events")
        .select("*, ticket_types(*)")
        .eq("is_published", true)
        .order("date", { ascending: true });
      setEvents((data as EventWithTicketTypes[]) || []);
    }
    setLoading(false);
  }

  const filteredEvents = events.filter((event) => {
    const matchesCategory = activeCategory === "Todos" || event.category === activeCategory;
    const matchesSearch =
      !searchQuery ||
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.city.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden pb-20 pt-20 sm:pt-28">
        <div className="absolute inset-0">
          <img src={heroBg} alt="" className="h-full w-full object-cover opacity-20" width={1920} height={960} />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
        </div>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-[120px]" />
          <div className="absolute bottom-0 right-0 h-[300px] w-[300px] rounded-full bg-violet-glow/5 blur-[80px]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-xs font-medium text-accent-foreground mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              Plataforma de experiencias con tickets inteligentes
            </div>
            <h1 className="text-4xl font-black tracking-tight text-foreground sm:text-6xl lg:text-7xl leading-[1.05]">
              Descubrí experiencias{" "}
              <span className="gradient-text">que importan</span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-muted-foreground sm:text-lg leading-relaxed">
              Comprá entradas seguras con QR dinámico, revendé de forma oficial y viví cada evento con tranquilidad.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#events"
                className="inline-flex items-center gap-2 rounded-xl gradient-primary px-6 py-3 text-sm font-bold text-primary-foreground transition-all hover:opacity-90 glow-primary"
              >
                Explorar eventos
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/validate"
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-secondary px-6 py-3 text-sm font-semibold text-secondary-foreground transition-colors hover:bg-accent"
              >
                Validar ticket
              </Link>
            </div>
          </div>

          {/* Features */}
          <div className="mt-16 grid gap-4 sm:grid-cols-3">
            {[
              { icon: Zap, title: "Compra en segundos", desc: "Flujo simplificado sin pasos innecesarios" },
              { icon: Shield, title: "Tickets seguros", desc: "QR dinámico único e intransferible por captura" },
              { icon: Sparkles, title: "Reventa oficial", desc: "Revendé tus entradas de forma segura y controlada" },
            ].map((f) => (
              <div key={f.title} className="rounded-xl border border-border bg-card p-5 transition-colors hover:bg-accent/50">
                <f.icon className="h-5 w-5 text-primary mb-2" />
                <h2 className="text-sm font-bold text-card-foreground">{f.title}</h2>
                <p className="mt-1 text-xs text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events */}
      <section id="events" className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-2xl font-bold text-foreground sm:text-3xl">Próximos eventos</h2>
          <div className="relative max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar evento o ciudad..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-border bg-input py-2.5 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {/* Category filters */}
        <div className="mt-5 flex gap-2 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                activeCategory === cat
                  ? "gradient-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground hover:bg-accent"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse rounded-2xl bg-card border border-border">
                <div className="aspect-[16/10] bg-muted rounded-t-2xl" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                  <div className="h-3 bg-muted rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filteredEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}

        {!loading && filteredEvents.length === 0 && (
          <div className="mt-12 text-center">
            <p className="text-muted-foreground">No se encontraron eventos para esta búsqueda.</p>
          </div>
        )}
      </section>
    </>
  );
}
